import type { Category, ExerciseConfig, ScoringSex } from './types';
import { normalizedLiftForPoints } from './categoryPoints';

/**
 * The entered weight that would score `targetPoints` for a single rep of
 * `exercise`. Used to seed the what-if calculator for a lift the user has never
 * logged, so the table opens in a meaningful range instead of at zero.
 *
 * This inverts scoreWorkout's non-bodyweight path. At one rep the Epley factor
 * is skipped entirely (estimateOneRepMax returns the weight itself), so:
 *
 *   points -> normalized 1RM        (normalizedLiftForPoints)
 *   normalized 1RM = entered x inputWeightMultiplier x normalizationMultiplier
 *
 * Bodyweight lifts are not covered: their resistance is the lifter, so there is
 * no added weight that "should" be the starting point - zero is the honest
 * default and the caller uses that instead.
 */
export function enteredWeightForPoints(
  targetPoints: number,
  bodyWeightLb: number,
  sex: ScoringSex,
  exercise: Pick<
    ExerciseConfig,
    'category' | 'normalizationMultiplier' | 'inputWeightMultiplier'
  > & { category: Category },
): number {
  const normalized1RmLb = normalizedLiftForPoints(
    targetPoints,
    bodyWeightLb,
    sex,
    exercise.category,
  );
  return normalized1RmLb / exercise.normalizationMultiplier / exercise.inputWeightMultiplier;
}

/**
 * A sensible starting weight for the what-if table: what a 100-point single
 * would take, rounded to the nearest 5 lb so it reads like a real loaded bar.
 */
export function seedWeightForExercise(
  bodyWeightLb: number,
  sex: ScoringSex,
  exercise: Pick<ExerciseConfig, 'category' | 'normalizationMultiplier' | 'inputWeightMultiplier'>,
): number {
  const exact = enteredWeightForPoints(100, bodyWeightLb, sex, exercise);
  return Math.max(5, Math.round(exact / 5) * 5);
}
